import { Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { InventorySummaryProxyService } from '../../proxies/inventory/inventory-summary.proxy.service';
import { LotItemProxyService } from '../../proxies/inventory/lot-item.proxy.service';
import { ConsumableLabelProxyService } from '../../proxies/inventory/consumable-label.proxy.service';
import { FormControl } from '@angular/forms';
import { ItemsInventoryService, InventoryViewMode } from '../../services/base-actions/items-inventory.service';
import { SelectButtonChangeEvent } from 'primeng/selectbutton';
import { PaginatorState } from 'primeng/paginator';
import { Translation } from '../../models/translation';
import { TranslateService } from '@ngx-translate/core';
import { LocalHelperService } from '../../services/local-helper.service';
import { MessageService } from 'primeng/api';
import { LotItemSearchResultItem } from '../../models/inventory/lot-item-search';
import { InventorySummarySearchResultItem, ReservedQuantityDetail } from '../../models/inventory/inventory-summary-search';
import { ItemsInventoryFiltersComponent } from '../common/items-inventory-filters/items-inventory-filters.component';

interface ColumnDefinition {
  field: string;
  header: string;
  translationKey: string;
}

@Component({
  selector: 'app-items-inventory',
  templateUrl: './items-inventory.component.html',
  styleUrl: './items-inventory.component.scss',
})
export class ItemsInventoryComponent {
  @ViewChild(ItemsInventoryFiltersComponent) filtersComponent!: ItemsInventoryFiltersComponent;

  isLoading = 0;
  items = [{ label: 'Inventory', route: '/' }, { label: 'Item Inventory' }];

  // Filters panel visibility. Kept mounted (CSS-hidden, not *ngIf) while toggled off — the
  // filters component drives triggerSearch() for pagination/view-mode changes, so unmounting it
  // would silently break those controls while the panel is hidden.
  showFilters = true;

  // View mode toggle
  viewModeControl: FormControl<InventoryViewMode | null> = new FormControl('lotItem');
  viewModeTranslations: Record<string, Translation> = {
    lotItem: { es: 'Lot Items', en: 'Lot Items' },
    consumables: { es: 'Consumibles', en: 'Consumables' },
  };
  viewModeOptions: { label: string; value: InventoryViewMode }[] = [];

  // Lot Item columns
  lotItemColumns: ColumnDefinition[] = [
    { field: 'number', header: 'Lot Item Number', translationKey: 'Items_Inventory_Lot_Item_ID' },
    { field: 'currentLocationName', header: 'Location', translationKey: 'Items_Inventory_Location_Name' },
    { field: 'itemCode', header: 'Item Code', translationKey: 'Items_Inventory_Item_Code' },
    { field: 'itemDisplayName', header: 'Item Description', translationKey: 'Items_Inventory_Item_Description' },
    { field: 'manufacturer', header: 'Manufacturer', translationKey: 'Manufacturer' },
    { field: 'manufacturerLotNumber', header: 'Mfg Lot #', translationKey: 'Manufacturer_Lot_Number' },
    { field: 'manufacturerLabelNumber', header: 'Mfg Label #', translationKey: 'Manufacturer_Label_ID' },
    { field: 'provider', header: 'Provider', translationKey: 'Items_Inventory_Provider' },
    { field: 'currentQuantity', header: 'Current Qty', translationKey: 'Items_Inventory_Net_Weight' },
    { field: 'originalQuantity', header: 'Original Qty', translationKey: 'Items_Inventory_Original_Quantity' },
  ];

  // Inventory Summary columns
  inventorySummaryColumns: ColumnDefinition[] = [
    { field: 'locationCode', header: 'Location Code', translationKey: 'Items_Inventory_Location_Code' },
    { field: 'locationName', header: 'Location Name', translationKey: 'Items_Inventory_Location_Name' },
    { field: 'itemCode', header: 'Item Code', translationKey: 'Items_Inventory_Item_Code' },
    { field: 'itemDisplayName', header: 'Item Description', translationKey: 'Items_Inventory_Item_Description' },
    { field: 'onHandQuantity', header: 'On Hand', translationKey: 'Items_Inventory_On_Hand' },
    { field: 'reservedQuantity', header: 'Reserved', translationKey: 'Items_Inventory_Reserved' },
    { field: 'availableQuantity', header: 'Available', translationKey: 'Items_Inventory_Available' },
  ];

  // Pagination
  pageSize = 50;
  page = 1;
  totalRecords = 0;
  pageInputValue: number | null = 1;

  constructor(
    private router: Router,
    private inventorySummaryProxyService: InventorySummaryProxyService,
    private lotItemProxyService: LotItemProxyService,
    private itemsInventoryService: ItemsInventoryService,
    private translateService: TranslateService,
    public localHelperTranslate: LocalHelperService,
    private messageService: MessageService,
    private consumableLabelProxyService: ConsumableLabelProxyService,
  ) {
    this.initViewModeOptions();
    this.translateService.onLangChange.subscribe(() => {
      this.initViewModeOptions();
    });
    this.pageSize = this.lotItemProxyService.lotItemSearchPageSize();
  }

  private initViewModeOptions(): void {
    const currentLang = (this.translateService.currentLang || 'en') as keyof Translation;
    this.viewModeOptions = [
      { label: this.viewModeTranslations['lotItem'][currentLang], value: 'lotItem' },
      { label: this.viewModeTranslations['consumables'][currentLang], value: 'inventorySummary' },
    ];
  }

  // View mode getters
  get currentViewMode(): InventoryViewMode {
    return this.itemsInventoryService.viewMode();
  }

  get lotItemResults(): LotItemSearchResultItem[] {
    return this.itemsInventoryService.lotItemResults();
  }

  get inventorySummaryResults(): InventorySummarySearchResultItem[] {
    return this.itemsInventoryService.inventorySummaryResults();
  }

  get currentColumns(): ColumnDefinition[] {
    return this.currentViewMode === 'lotItem' ? this.lotItemColumns : this.inventorySummaryColumns;
  }

  get hasResults(): boolean {
    if (this.currentViewMode === 'lotItem') {
      return this.lotItemResults.length > 0;
    }
    return this.inventorySummaryResults.length > 0;
  }

  get isLastPage(): boolean {
    return this.page * this.pageSize >= this.totalRecords;
  }

  get totalPages(): number {
    return Math.ceil(this.totalRecords / this.pageSize) || 1;
  }

  get isFirstPage(): boolean {
    return this.page === 1;
  }

  // View mode change handler
  onViewModeChange(event: SelectButtonChangeEvent): void {
    const newMode = event.value as InventoryViewMode;
    this.itemsInventoryService.setViewMode(newMode);
    this.resetPagination();

    // Trigger search via filters component
    if (this.filtersComponent) {
      this.filtersComponent.triggerSearch();
    }
  }

  // Loading state handlers
  onLoadingChange(loading: number): void {
    this.isLoading = loading;
  }

  onTotalRecordsChange(total: number): void {
    this.totalRecords = total;
  }

  // Pagination
  private resetPagination(): void {
    this.page = 1;
    if (this.currentViewMode === 'lotItem') {
      this.lotItemProxyService.lotItemSearchPage.set(1);
    } else {
      this.inventorySummaryProxyService.inventorySummarySearchPage.set(1);
    }
  }

  setSearchPageSize(event: PaginatorState): void {
    this.pageSize = event.rows || 50;
    this.page = 1;

    if (this.currentViewMode === 'lotItem') {
      this.lotItemProxyService.lotItemSearchPageSize.set(this.pageSize);
      this.lotItemProxyService.lotItemSearchPage.set(1);
    } else {
      this.inventorySummaryProxyService.inventorySummarySearchPageSize.set(this.pageSize);
      this.inventorySummaryProxyService.inventorySummarySearchPage.set(1);
    }

    if (this.filtersComponent) {
      this.filtersComponent.triggerSearch();
    }
  }

  nextPage(): void {
    this.page++;
    this.pageInputValue = this.page;

    if (this.currentViewMode === 'lotItem') {
      this.lotItemProxyService.lotItemSearchPage.set(this.page);
    } else {
      this.inventorySummaryProxyService.inventorySummarySearchPage.set(this.page);
    }

    if (this.filtersComponent) {
      this.filtersComponent.triggerSearch();
    }
  }

  prevPage(): void {
    this.page--;
    this.pageInputValue = this.page;

    if (this.currentViewMode === 'lotItem') {
      this.lotItemProxyService.lotItemSearchPage.set(this.page);
    } else {
      this.inventorySummaryProxyService.inventorySummarySearchPage.set(this.page);
    }

    if (this.filtersComponent) {
      this.filtersComponent.triggerSearch();
    }
  }

  resetTable(): void {
    this.page = 1;
    this.pageSize = 50;
    this.pageInputValue = 1;

    if (this.currentViewMode === 'lotItem') {
      this.lotItemProxyService.lotItemSearchPage.set(1);
      this.lotItemProxyService.lotItemSearchPageSize.set(50);
    } else {
      this.inventorySummaryProxyService.inventorySummarySearchPage.set(1);
      this.inventorySummaryProxyService.inventorySummarySearchPageSize.set(50);
    }

    if (this.filtersComponent) {
      this.filtersComponent.triggerSearch();
    }
  }

  firstPage(): void {
    if (this.page === 1) return;

    this.page = 1;
    this.pageInputValue = 1;

    if (this.currentViewMode === 'lotItem') {
      this.lotItemProxyService.lotItemSearchPage.set(1);
    } else {
      this.inventorySummaryProxyService.inventorySummarySearchPage.set(1);
    }

    if (this.filtersComponent) {
      this.filtersComponent.triggerSearch();
    }
  }

  lastPage(): void {
    const lastPageNum = this.totalPages;
    if (this.page === lastPageNum) return;

    this.page = lastPageNum;
    this.pageInputValue = lastPageNum;

    if (this.currentViewMode === 'lotItem') {
      this.lotItemProxyService.lotItemSearchPage.set(lastPageNum);
    } else {
      this.inventorySummaryProxyService.inventorySummarySearchPage.set(lastPageNum);
    }

    if (this.filtersComponent) {
      this.filtersComponent.triggerSearch();
    }
  }

  goToPage(): void {
    const targetPage = this.pageInputValue;
    if (targetPage === null || targetPage < 1 || targetPage > this.totalPages || targetPage === this.page) {
      this.pageInputValue = this.page;
      return;
    }

    this.page = targetPage;

    if (this.currentViewMode === 'lotItem') {
      this.lotItemProxyService.lotItemSearchPage.set(targetPage);
    } else {
      this.inventorySummaryProxyService.inventorySummarySearchPage.set(targetPage);
    }

    if (this.filtersComponent) {
      this.filtersComponent.triggerSearch();
    }
  }

  onPageInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.goToPage();
    }
  }

  // Navigation
  redirectToLotDetail(id?: string): void {
    const queryParams = id ? { id: id } : {};
    this.router.navigate(['/items-inventory-detail'], { queryParams });
  }

  // Export
  exportToExcel(): void {
    this.isLoading++;

    if (this.currentViewMode === 'lotItem') {
      const filters = this.itemsInventoryService.lotItemSearchFilters();
      this.lotItemProxyService.exportToExcelPaginated(filters).subscribe({
        next: (blob) => {
          this.downloadBlob(blob, 'lot-items-export.xlsx');
          this.isLoading--;
        },
        error: (error) => {
          console.error('Error exporting lot items:', error);
          this.isLoading--;
        },
      });
    } else {
      const filters = this.itemsInventoryService.inventorySummarySearchFilters();
      this.inventorySummaryProxyService.exportToExcelPaginated(filters).subscribe({
        next: (blob) => {
          this.downloadBlob(blob, 'inventory-summary-export.xlsx');
          this.isLoading--;
        },
        error: (error) => {
          console.error('Error exporting inventory summary:', error);
          this.isLoading--;
        },
      });
    }
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
    this.messageService.add({
      severity: 'success',
      summary: 'Export Complete',
      detail: `File "${filename}" has been downloaded.`,
      life: 3000,
    });
  }

  // Helper methods for displaying data
  getLocationName(item: LotItemSearchResultItem): string {
    if (item.currentLocationName) {
      return this.localHelperTranslate.getLocalizedValue(item.currentLocationName);
    }
    return item.currentLocationCode || '';
  }

  getItemDisplayName(item: LotItemSearchResultItem | InventorySummarySearchResultItem): string {
    if (item.itemDisplayName) {
      return this.localHelperTranslate.getLocalizedValue(item.itemDisplayName);
    }
    return '';
  }

  getManufacturerName(item: LotItemSearchResultItem): string {
    return item.manufacturer?.name || '';
  }

  getProviderName(item: LotItemSearchResultItem): string {
    return item.provider?.name || '';
  }

  getSummaryLocationName(item: InventorySummarySearchResultItem): string {
    if (item.locationName) {
      return this.localHelperTranslate.getLocalizedValue(item.locationName);
    }
    return item.locationCode || '';
  }

  // Reserved quantity details helpers
  hasReservedDetails(item: InventorySummarySearchResultItem): boolean {
    return item.reservedQuantity > 0 && item.reservedQuantityDetails && item.reservedQuantityDetails.length > 0;
  }

  getReservedDetails(item: InventorySummarySearchResultItem): ReservedQuantityDetail[] {
    return item.reservedQuantityDetails || [];
  }

  getOwnerReference(detail: ReservedQuantityDetail): string {
    return detail.owner?.number || 'Unknown';
  }

  formatReservedDate(detail: ReservedQuantityDetail): string {
    if (!detail.reservedAt) return '';
    const date = new Date(detail.reservedAt);
    return date.toLocaleDateString();
  }

  printLotItemLabel(item: LotItemSearchResultItem) {
    this.isLoading++;
    this.lotItemProxyService.getByLotItemNumber(item.number).subscribe({
      next: (response) => {
        if (response.data?.id) {
          this.lotItemProxyService.getLotItemLabel(response.data.id).subscribe({
            next: (blob) => {
              const url = window.URL.createObjectURL(blob);
              window.open(url, '_blank');
              setTimeout(() => window.URL.revokeObjectURL(url), 10000);
              this.isLoading--;
            },
            error: (error) => {
              console.error('Error downloading lot item label:', error);
              this.isLoading--;
            },
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Lot item not found',
            life: 3000,
          });
          this.isLoading--;
        }
      },
      error: (error) => {
        console.error('Error fetching lot item:', error);
        this.isLoading--;
      },
    });
  }

  printConsumableLabel(item: InventorySummarySearchResultItem) {
    this.isLoading++;
    this.consumableLabelProxyService.getConsumableLabel(item.itemCode, item.locationCode).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
        this.isLoading--;
      },
      error: (error) => {
        console.error('Error downloading consumable label:', error);
        this.isLoading--;
      },
    });
  }
}
